import { useContext, createContext, useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "./useAuth";
import { useChats } from "./useChats";
import { stitchSegmentsStreaming, shouldWarnPartialLoss } from "../utils/audioStitch";
import { stitchWithRetry } from "../utils/stitchWithRetry";
import { groupRecordings, queuedAudioSecs } from "../utils/recordingList";
import { isRecordingAutoRecoverEnabledFromStorage } from "../utils/recordingAutoRecoverFlag";
import { isHeartbeatFresh, freshHeartbeatIds, getActiveRecording } from "../utils/recordingHeartbeat";
import {
  shouldRebuildFinalless,
  recoveryTimeoutMs,
  msUntilRebuildEligible,
  STALE_REBUILD_MS,
} from "../utils/recordingRebuildGate";
import { wasCaptureInterrupted, clearCaptureInterrupted } from "../utils/recordingInterrupted";
import {
  claimRecoveryLock,
  releaseRecoveryLock,
  getGiveUp,
  bumpGiveUp,
  clearGiveUp,
  RECOVERY_LOCK_TTL_MS,
  MAX_RECOVERY_GIVEUP,
} from "../utils/recordingRecoveryState";
import {
  isPermanentRecoveryError,
  isHttp404,
  confirmEncounterGone,
  resolveRecordingChatId,
  RECORDING_SAVE_DEFERRED_OFFLINE,
} from "../utils/recordingSaveError";
import { markRecentlyRecovered } from "../utils/recordingRecoveredNotice";
import { missingIndices, buildSegmentSources } from "../utils/segmentGapFill";
import {
  bumpPartialAttempts,
  clearPartialAttempts,
  partialAttemptsExhausted,
  MAX_PARTIAL_ATTEMPTS,
} from "../utils/recordingPartialRetry";
import { requestPersistentStorage } from "../utils/persistentStorage";
import { isQuotaError, reclaimCacheSpace } from "../utils/storageReclaim";
import { audioFileName, dataUrlToBlob, saveAudioFile } from "../utils/saveAudioFile";
import { buildScope } from "../utils/offlineCache";
// promisifyRequest / awaitTransaction moved to lucasDb.js verbatim so the
// offline caches share one implementation; behavior here is unchanged.
import { awaitTransaction, openLucasDb, promisifyRequest } from "../utils/lucasDb";
import { subscribe, unsubscribe } from "../utils/events";
import api from "../services/api";
// Stamps captured recordings and gates Path A's finalless rebuild (fail CLOSED on
// an absent/foreign org, spec §14C). Shared with the Local Recordings modal so its
// Retry offer and this drain's decision are made from the same value.
import { currentOrgId } from "../utils/currentOrgId";
import { recordWebRecordingSaved } from "../utils/webRecordingCustody";
import { hasLiveWebRecordingOwner } from "../utils/webRecordingOwnership";
import { isIdentityRefusal } from "../utils/recordingIdentityGuard";

export const IndexDbContext = createContext();

// Retry the upload of failed recordings with exponential backoff so a
// persistently-failing recording (offline, expired session) does not hammer
// the server every 20s, while still recovering on its own.
const RETRY_BASE_MS = 20000;
const RETRY_MAX_MS = 5 * 60 * 1000;
// After this many failed attempts we warn the clinician once, but we never stop
// retrying — abandoning the recording silently meant permanent audio loss.
const WARN_AFTER_ERRORS = 3;
// Hard ceiling on a single parseBlob attempt. parseBlob can hang indefinitely if a
// step never settles (e.g. a decode/encode that neither resolves nor rejects); that
// hang holds the `syncing` guard true forever and freezes the whole queue. The race
// below forces the attempt to fail (and reschedule) instead of deadlocking. The
// upload is async on the backend, so a healthy attempt resolves in seconds — this
// only ever fires on a genuine hang.
const PARSE_TIMEOUT_MS = 3 * 60 * 1000;
// On a shared device, a recording queued by one user can never be synced by the
// next (the queue is read by the logged-in user's id). We keep the CURRENT user's
// recordings indefinitely (never abandon audio), but purge another user's leftover
// recordings after this window so their audio (PHI) does not linger in the browser.
const FOREIGN_RECORDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Each recorded segment is ~SEGMENT_MS (useRecorder.js) = 10s. Used only to
// estimate total duration for the encoder's bitrate bucket — a coarse estimate
// is fine (buckets are 30/60/120-minute wide).
const SEGMENT_SECS = 10;
// A queued offline recording carries a browser-only `temp-*` chatId until its
// encounter syncs and the server assigns a durable id. Resolution can only succeed
// AFTER that sync, so we retry patiently — but not forever: a draft the user deleted
// (or a lookup that keeps failing on bad org data) would otherwise re-attempt every
// backoff tick indefinitely. Once a row has been queued longer than this, stop
// treating an unresolved temp id as "still syncing" and let it fall through to the
// bounded give-up / error path. The audio is KEPT and stays retryable from the modal.
const CHAT_ID_RESOLUTION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// How much un-synced audio has to be sitting on the device before the clinician is
// interrupted at sign-in rather than merely toasted. Below this the existing
// summary toast is the right weight. Above it the device is holding a meaningful
// amount of clinical audio that has reached no encounter, and a notice that
// auto-closes in six seconds has demonstrably not been enough: one clinician
// carried ~13 hours of it for weeks, on a browser that had also refused
// persistent storage, and only surfaced it by asking support why nothing worked.
// Exported for the regression test.
export const BACKLOG_ALERT_SECS = 30 * 60;
// Parked rows -- `orphaned` (encounter confirmed deleted) and `undecodable` (bytes
// that will never decode) -- are skipped by the drain forever and nothing ever
// removed them, so the queue could only grow. One clinician reached 4728 rows
// (~13h of base64 audio) on a browser that had also been DENIED persistent
// storage. Both kinds were toasted at the time and stay listed in Local Recordings
// (where Retry and Save audio work) for this whole window; past it they are audio
// that can never reach a transcript, so keeping them only leaves PHI in the browser
// and slows every later pass -- the same reasoning as FOREIGN_RECORDING_TTL_MS,
// with a far longer fuse because this is the CURRENT user's own audio.
// Exported for the regression test.
export const PARKED_RECORDING_TTL_MS = 30 * 24 * 60 * 60 * 1000;
// Depth at which a queue stops being "a few recordings waiting for the network"
// and becomes a fault worth reporting. ~90 minutes of segments. The depth was
// always printed ("Found N unsynced recordings") but at console.log, which Sentry's
// captureConsoleIntegration does not collect -- so 4728 rows were only ever visible
// as a breadcrumb on an unrelated error. Exported for the regression test.
export const QUEUE_DEPTH_WARN_ROWS = 500;

// Two recording snapshots are display-equal when the same logical recordings are
// present, in order, with the same status and error/segment counts. Used to bail
// out of a no-op setRecordings so a permanently-stuck recording doesn't re-render
// the recorder subtree (ProvideIndexDb wraps ProvideRecorder) on every backoff tick.
function sameRecordingList(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].id !== b[i].id ||
      a[i].status !== b[i].status ||
      a[i].errorsCount !== b[i].errorsCount ||
      a[i].segmentCount !== b[i].segmentCount
    ) {
      return false;
    }
  }
  return true;
}

// True once a queued recording has waited past CHAT_ID_RESOLUTION_MAX_AGE_MS for its
// offline encounter's durable id. A missing timestamp (older rows) reads as NOT
// exhausted — never abandon audio on ambiguity.
function chatIdResolutionExhausted(recording) {
  const queuedAt = recording && recording.date ? new Date(recording.date).getTime() : NaN;
  if (!Number.isFinite(queuedAt)) return false;
  return Date.now() - queuedAt > CHAT_ID_RESOLUTION_MAX_AGE_MS;
}

function useIndexDbProvider() {
  const { t } = useTranslation();
  const [db, setDb] = useState(null);
  // The handle lives in a ref as well as state: a heal has to be visible to code
  // that is ALREADY running (a re-render is too late for the pass in flight), while
  // the state keeps the effects' dependencies honest.
  const dbRef = useRef(null);
  // isServerAvailable gates the storage reclaim below: the offline caches may only
  // be dropped for space when the app can refetch them.
  const { auth, isDemoRef, brainId, isServerAvailable } = useAuth();
  const { parseBlob, parseRecording } = useChats();
  let timeoutRetry = useRef(null);
  // False once this provider has unmounted. The drain is a self-rescheduling loop
  // with several await boundaries, so a pass can still be in flight — and a retry
  // still armed — when the component goes away. Work that lands after that has
  // nothing to act on: it logs against a dead component, and in a test run it lands
  // after teardown, which is what turned a fully passing CI run into exit code 1.
  const alive = useRef(true);
  let retryAttempt = useRef(0);
  let sessionWarned = useRef(false);
  // Deleted-encounter notice: tell the clinician once per session, not once
  // per orphaned row per drain pass.
  let orphanWarned = useRef(false);
  // Same shape: one notice per session when a recording is parked by the identity guard.
  let identityParkWarned = useRef(false);
  // Same shape as orphanWarned: one notice per session, not one per drain tick.
  let undecodableWarned = useRef(false);
  let syncing = useRef(false);
  let rescanRequested = useRef(false);

  // Desktop Local Recordings manager state. `recordings` mirrors the queue as
  // logical recordings (segments grouped) for the modal + sidebar badge.
  const [recordings, setRecordings] = useState([]);
  const [showRecordingsModal, setShowRecordingsModal] = useState(false);
  // The login drain re-attempts every queued recording. Instead of one toast per
  // stuck recording (the flood), we show ONE summary toast per failing session;
  // this ref throttles it (reset when progress is made or a new recording is added).
  let summaryShown = useRef(false);
  // recordingIds whose recovery failed PERMANENTLY this session (the encounter was
  // signed/archived, so Transcript - Save can never accept it). Skip them on later
  // passes and warn ONCE. In-session only — a reload re-attempts once, which the BE
  // rejects cleanly (no storm). Replaces the warnedKeys ref #2404 removed.
  let recoveryPermSkip = useRef(new Set());
  // The sign-in backlog alert. Fires at most once per session, and only AFTER a
  // drain pass has completed — a queue that is about to upload normally is not a
  // backlog, and interrupting someone about it would teach them to dismiss this.
  const [showBacklogAlert, setShowBacklogAlert] = useState(false);
  let backlogAlerted = useRef(false);
  // Armed by the sign-in effect and consumed by the first pass that runs while no
  // capture is active in this tab. The alert is a SIGN-IN question; the check used
  // to run at the end of every pass, including the one each 10s segment write
  // triggers, so the modal could pop in the middle of a dictation.
  let backlogCheckArmed = useRef(false);
  // One report per session for a queue that has grown past QUEUE_DEPTH_WARN_ROWS —
  // the drain runs every 10s during a recording, so an unthrottled report would be
  // its own flood.
  let queueDepthWarned = useRef(false);
  // The metadata scan's degraded path (scanQueueMetadata): one report per session
  // when the cursor fails, one when a row has to be skipped — a pass runs every
  // backoff tick, so per-pass logging would flood.
  let scanFallbackWarned = useRef(false);
  let unreadableRowWarned = useRef(false);
  // Bulk-save progress shared by every consumer (Local Recordings + the sign-in
  // backlog alert): null when idle, {done, total} while running. Lives here so the
  // two surfaces see ONE flag — each used to hold its own, so a Save all started
  // from one left the other's controls live.
  const [bulkSave, setBulkSave] = useState(null);

  const retryCheck = () => {
    if (!alive.current) return;
    if (timeoutRetry.current) {
      clearTimeout(timeoutRetry.current);
    }

    const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** retryAttempt.current);
    retryAttempt.current += 1;
    console.log(`Scheduling retry for checking recordings in ${Math.round(delay / 1000)}s`);

    timeoutRetry.current = setTimeout(() => {
      checkRecordings();
    }, delay);
  };

  // Is this handle still usable? A connection closed by another tab's upgrade
  // (lucasDb's onversionchange) stays truthy but throws InvalidStateError on every
  // transaction, so "we hold a handle" is not the same as "we can write". Creating
  // and immediately aborting a readonly transaction costs nothing and answers it.
  const dbIsUsable = (handle) => {
    if (!handle) return false;
    try {
      handle.transaction(["recordings"], "readonly").abort();
      return true;
    } catch (e) {
      return false;
    }
  };

  // THE way to reach the database — re-opening on demand rather than waiting for a
  // remount.
  //
  // openLucasDb resolves NULL when the open is BLOCKED: another context is holding
  // the database at an older version, which happens whenever a deploy bumps
  // LUCAS_DB_VERSION while a clinician still has yesterday's bundle open somewhere
  // (a second tab, or the Chrome extension side panel — it shares this exact
  // database, unpartitioned). The block clears by itself once that context closes
  // its connection, and lucasDb republishes the live handle for the next caller.
  //
  // Nothing ever asked again. A session that started inside that window ran to its
  // end with db === null, so every recording segment failed to persist — reported to
  // the clinician as "storage may be full", and to Sentry as 25 lost segments in a
  // row (LUCASHEALTH-V5: 75 events, 3 users, production). The audio survived only
  // because the server backstop had its own copy.
  const ensureDb = async () => {
    if (dbIsUsable(dbRef.current)) return dbRef.current;
    const handle = await openLucasDb();
    dbRef.current = handle || null;
    // Push it into state too: the effects that arm the drain key on it.
    if (handle && handle !== db) setDb(handle);
    return handle;
  };

  // A finalless set becomes rebuildable purely by getting older, and time fires no
  // event. This is the timer that watches that clock: armed for the moment the set
  // qualifies (plus a second of slack), replaced whenever a nearer deadline appears,
  // and separate from the backoff timer above so an upload retry and an eligibility
  // wait never overwrite each other.
  const eligibilityTimer = useRef(null);
  const eligibilityDeadline = useRef(0);
  const scheduleEligibilityRecheck = (waitMs) => {
    const wait = Math.max(1000, Math.min(waitMs + 1000, STALE_REBUILD_MS + 1000));
    const deadline = Date.now() + wait;
    // Keep the SOONEST deadline: several finalless sets can be waiting at once.
    if (eligibilityTimer.current && eligibilityDeadline.current <= deadline) return;
    if (eligibilityTimer.current) clearTimeout(eligibilityTimer.current);
    eligibilityDeadline.current = deadline;
    console.log(`Finalless recording not yet rebuildable; re-checking in ${Math.round(wait / 1000)}s`);
    eligibilityTimer.current = setTimeout(() => {
      eligibilityTimer.current = null;
      eligibilityDeadline.current = 0;
      checkRecordings();
    }, wait);
  };
  // Every timer this provider owns dies with it. The drain reschedules itself
  // indefinitely by design — never abandon audio — but a provider that has UNMOUNTED
  // has no business waking up: the work runs against a dead component, and in tests
  // it lands after teardown, which is what broke CI on 2026-08-19 (all 671 suites
  // passing, exit code 1, from logs written after the run).
  useEffect(
    () => () => {
      alive.current = false;
      if (eligibilityTimer.current) clearTimeout(eligibilityTimer.current);
      if (timeoutRetry.current) clearTimeout(timeoutRetry.current);
    },
    []
  );

  // Drop another user's stale recordings so their audio (PHI) does not persist in
  // this browser. Only foreign + expired records are removed; the current user's
  // pending recordings are never touched here.
  const cleanupForeignRecordings = async (currentUserId) => {
    if (!currentUserId || !(await ensureDb())) return;
    try {
      const tx = dbRef.current.transaction(["recordings"], "readwrite");
      const cursorReq = tx.objectStore("recordings").openCursor();
      await new Promise((resolve, reject) => {
        cursorReq.onsuccess = (event) => {
          const cursor = event.target.result;
          if (!cursor) return resolve();
          const r = cursor.value;
          const ts = r?.date instanceof Date ? r.date.getTime() : new Date(r?.date || 0).getTime();
          const age = Date.now() - ts;
          if (r?.userId && r.userId !== currentUserId && age > FOREIGN_RECORDING_TTL_MS) {
            cursor.delete();
          }
          cursor.continue();
        };
        cursorReq.onerror = (event) => reject(event.target?.error || event);
      });
    } catch (e) {
      console.error("Error cleaning up foreign recordings", e);
    }
  };

  useEffect(() => {
    // Ask the browser not to evict this origin's storage. Queued web recordings
    // live in the IndexedDB store opened below; without a persistent grant a
    // browser under storage pressure (Safari worst) can silently drop a
    // not-yet-uploaded recording. Fire-and-forget and self-guarded — it never
    // throws and logs only the grant status (no PHI).
    requestPersistentStorage();

    // Initialize IndexedDB via the shared opener (lucasDb.js owns the schema;
    // v2 added the offline caches — the recordings store is unchanged). A failure
    // here is no longer terminal for the session: ensureDb re-opens on demand.
    ensureDb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth?.user?.id) {
      try {
        cleanupForeignRecordings(auth.user.id);
        refreshRecordings();
        // The backlog alert is asked exactly once per sign-in, by the first pass
        // that runs with no capture active in this tab (see the end of
        // checkRecordings). Armed here, not inside the pass, so the segment-write
        // passes that follow can never pop it mid-dictation.
        backlogCheckArmed.current = true;
        checkRecordings();
      } catch (e) {
        console.error("Error checking recordings", e);
      }
    } else {
      if (timeoutRetry.current) {
        clearTimeout(timeoutRetry.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.id]);

  useEffect(() => {
    const onReconnect = () => {
      if (auth?.user?.id) {
        // Connectivity restored or tab refocused: drop the backoff and try now.
        retryAttempt.current = 0;
        checkRecordings();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") onReconnect();
    };
    window.addEventListener("online", onReconnect);
    document.addEventListener("visibilitychange", onVisible);
    // Server recovered from an outage (spec §4 step 3): the device may have
    // stayed online the whole time, so the "online" event never fires.
    subscribe("serverBackOnline", onReconnect);
    return () => {
      window.removeEventListener("online", onReconnect);
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribe("serverBackOnline", onReconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.id]);

  // Not more than once a minute: a device that is genuinely full fails a write
  // every SEGMENT_MS, and a full cache scan per failure would burn the CPU of the
  // machine that is already struggling. One pass frees everything a pass can.
  const RECLAIM_COOLDOWN_MS = 60000;
  const lastReclaimAt = useRef(0);

  // Free space for audio by dropping REGENERABLE caches — never a recording. The
  // current user+org's own cache is only dropped when the app can refetch it;
  // offline, that copy is the thing offline mode runs on. Returns whether anything
  // was actually freed, so the caller only retries a write that now has room.
  const reclaimSpaceForAudio = async () => {
    const now = Date.now();
    if (now - lastReclaimAt.current < RECLAIM_COOLDOWN_MS) return false;
    lastReclaimAt.current = now;
    const currentScope = buildScope(auth?.user?.id, currentOrgId());
    const freed = await reclaimCacheSpace(dbRef.current, {
      currentScope,
      // isServerAvailable, not isOnline: the caches are refilled by server calls.
      includeCurrentScope: !!isServerAvailable,
    });
    console.log(`Storage reclaim for recording audio freed ${freed} cache row(s)`);
    return freed > 0;
  };

  // Persist a whole recording to the durable queue and return only once the write
  // has COMMITTED (resolves true) or failed (resolves false). Callers write-ahead:
  // the audio is on disk before any upload is attempted, so a hung upload, a
  // closed tab, or a flaky network can never lose it. A successful write kicks an
  // immediate drain; the exponential-backoff timer is the backstop.
  const addRecording = async (record) => {
    if (!(await ensureDb())) {
      console.error("Database not initialized, while adding recording");
      return false;
    }

    // A freshly-captured recording should retry promptly, not on a stale backoff.
    retryAttempt.current = 0;

    try {
      const userId = record.userId || auth?.user?.id;
      if (!userId) {
        // The drain reads the queue by the logged-in user's id
        // (index("userId").getAllKeys(auth.user.id)), so a userId-less row would
        // be invisible to it forever. Refuse to queue and return false so the
        // caller falls back to a live attempt instead of reporting a false "saved".
        console.warn("No userId for recording; not durably queuing (caller should attempt live upload)");
        return false;
      }
      // New captures snapshot their organization before async capture/finalization.
      // Preserve even an explicit null; only legacy callers use the current org.
      const orgId = Object.prototype.hasOwnProperty.call(record, "orgId") ? record.orgId : currentOrgId();

      // Convert Blob to ArrayBuffer to avoid Safari iOS corruption
      const arrayBuffer = await record.blob.arrayBuffer();
      const blobType = record.blob.type;

      const writeRow = async () => {
        const transaction = dbRef.current.transaction(["recordings"], "readwrite");
        transaction.objectStore("recordings").add({
          chatId: record.chatId,
          userId,
          blobData: arrayBuffer,
          blobType: blobType,
          date: new Date(),
          state: "error",
          phase: record.phase,
          extraData: record.extraData,
          recordingId: record.recordingId,
          // Recording length (seconds) stamped at capture so the manager can show it
          // without decoding the audio. Optional — older queued rows lack it.
          durationSecs: typeof record.durationSecs === "number" ? record.durationSecs : undefined,
          // Capturing org — Path A refuses to rebuild a recording whose orgId is
          // absent or != the current org (fails closed, spec §14C). Older rows lack it.
          orgId,
          // Who and when this audio is for, captured at record time. Encounter ids
          // are recycled, so the drain's identity guard and the Recordings modal
          // read these instead of trusting the chatId. Older rows lack them.
          startedAt: typeof record.startedAt === "number" ? record.startedAt : undefined,
          patientUserId: record.patientUserId || undefined,
          patientLabel: record.patientLabel || undefined,
          encounterUuid: record.encounterUuid || undefined,
          errorsCount: 0,
        });
        // Wait for the TRANSACTION to commit (not just the request) so we only
        // report success once the write is durable.
        await awaitTransaction(transaction);
      };

      // A quota refusal is not necessarily final: drop the regenerable caches and
      // try once more before telling the caller the audio could not be stored.
      try {
        await writeRow();
      } catch (error) {
        if (!isQuotaError(error) || !(await reclaimSpaceForAudio())) throw error;
        console.warn("Retrying recording write after reclaiming cache space");
        await writeRow();
      }

      console.log("Recording persisted to durable queue", record.recordingId);
      checkRecordings();
      summaryShown.current = false;
      refreshRecordings();
      return true;
    } catch (error) {
      // QuotaExceededError or any IDB failure: the audio is NOT safely stored, so
      // surface it rather than pretend it was saved. The blob is still in the
      // caller's memory for a last-resort inline attempt.
      console.error("Error persisting recording to IndexedDB", error?.name || "", error);
      toast.error(t("hooks.useIndexDb.errorSavingRecording"));
      return false;
    }
  };

  // Queue a single recording SEGMENT that failed to upload live. Segments share
  // the "recordings" store (and therefore the whole resilient drain: backoff,
  // online/visibility wake, never-abandon, foreign-user cleanup) but carry
  // kind:"segment" so checkRecordings stitches them client-side and transcribes
  // the assembled recording once via the parseBlob path. The audio is already a
  // base64 data URL (text), so it stores directly — no ArrayBuffer conversion.
  const addRecordingSegment = async (record) => {
    if (!(await ensureDb())) {
      // Reached only when the re-open ALSO failed (the blocking context is still
      // there). The caller keeps the audio in memory, retries this write on the next
      // rotation, and has already streamed the segment to the server backstop.
      console.error("Database not initialized, while adding recording segment");
      return false;
    }
    retryAttempt.current = 0;

    try {
      const userId = record.userId || auth?.user?.id;
      if (!userId) {
        // Same as addRecording: a userId-less row is invisible to the drain.
        // Return false so flushSegment falls back to a direct upload.
        console.warn("No userId for segment; not durably queuing (caller should attempt live upload)");
        return false;
      }
      const orgId = Object.prototype.hasOwnProperty.call(record, "orgId") ? record.orgId : currentOrgId();

      const writeRow = async () => {
        const transaction = dbRef.current.transaction(["recordings"], "readwrite");
        transaction.objectStore("recordings").add({
          kind: "segment",
          chatId: record.chatId,
          userId,
          recordingId: record.recordingId,
          index: record.index,
          isFinal: !!record.isFinal,
          total: record.total || 0,
          language: record.language || "en",
          phase: record.phase || "",
          segmentAudio: record.segmentAudio,
          // Real recording-start time (ms epoch). Persisted with the queued segment
          // so an offline recording is stamped with WHEN IT WAS MADE on resync, not
          // the sync clock — otherwise several offline recordings draining together
          // would share a timestamp.
          startedAt: record.startedAt,
          // Capturing org — see addRecording. Path A's org-match guard (spec §14C).
          orgId,
          // Captured patient — see addRecording. Older rows lack it.
          patientUserId: record.patientUserId || undefined,
          patientLabel: record.patientLabel || undefined,
          encounterUuid: record.encounterUuid || undefined,
          date: new Date(),
          state: "error",
          errorsCount: 0,
        });
        // Wait for the commit so the caller knows the segment is durably stored.
        await awaitTransaction(transaction);
      };

      // Same quota escape hatch as addRecording: the caches are refetchable, this
      // segment is not. Free them and try once more before giving up on the audio.
      try {
        await writeRow();
      } catch (error) {
        if (!isQuotaError(error) || !(await reclaimSpaceForAudio())) throw error;
        console.warn("Retrying segment write after reclaiming cache space");
        await writeRow();
      }

      checkRecordings();
      summaryShown.current = false;
      refreshRecordings();
      return true;
    } catch (error) {
      // No toast here: flushSegment fires a segment every ~SEGMENT_MS, so a durable
      // failure (e.g. quota) would stack one error toast per segment. The caller
      // surfaces the failure to the user; the whole-recording path still toasts.
      console.error("Error persisting recording segment to IndexedDB", error?.name || "", error);
      return false;
    }
  };

  // The keys (in index order) of one recording's segments, WITHOUT loading any
  // audio. A cursor reads rows one at a time and we keep only {key, index}, so a
  // 60-min recording's base64 never lands in memory all at once.
  // The only index is on userId, so this scans all of the current user's segment
  // rows and filters by recordingId in JS — same characteristic as the prior
  // getSegmentsForRecording. Audio is never retained so memory stays bounded; a
  // [userId, recordingId] compound index would scope the scan but is a schema
  // migration (out of scope here).
  // Deliberately NOT served from the drain's metadata snapshot. Both call sites go
  // on to ASSEMBLE and DELETE this recording's rows, so they need the store as it is
  // NOW, not as it was when the pass began — answering from the snapshot would widen
  // the staleness window from one iteration to a whole pass. It runs at most once
  // per eligible recording; the cost that mattered was the per-key full-row reads.
  const getSegmentKeysForRecording = (recordingId) => {
    return new Promise((resolve, reject) => {
      const tx = dbRef.current.transaction(["recordings"], "readonly");
      const index = tx.objectStore("recordings").index("userId");
      const req = index.openCursor(IDBKeyRange.only(auth.user.id));
      const rows = [];
      req.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) {
          rows.sort((a, b) => a.index - b.index);
          resolve(rows);
          return;
        }
        const r = cursor.value;
        if (r && r.kind === "segment" && r.recordingId === recordingId) {
          rows.push({
            key: cursor.primaryKey,
            index: r.index,
            userId: r.userId,
            orgId: r.orgId,
            // Path A's gate needs these: isFinal distinguishes a finalless (crashed)
            // set from a complete one; date drives the ~120s live-recording guard.
            isFinal: !!r.isFinal,
            date: r.date ? new Date(r.date).getTime() : 0,
          });
        }
        cursor.continue();
      };
      req.onerror = (event) => reject(event.target?.error || event);
    });
  };

  // ONE metadata pass over this user's queue: everything the drain and the modal
  // decide with, and NONE of the audio (segmentAudio / blobData / blob / extraData
  // are dropped rather than carried).
  //
  // This replaces `getAllKeys` + a `store.get(key)` per key. That `get` deserializes
  // the whole record -- ~10s of base64 audio -- for every row, INCLUDING rows the
  // loop was about to skip as parked, and the drain runs once per 10s segment
  // rotation, so the cost of a recording of N segments was quadratic in N. At the
  // 4728 rows seen in production that was hundreds of MB re-read on the main thread
  // every 10 seconds, while MediaRecorder was trying to encode on the same thread.
  //
  // A cursor still deserializes each row internally -- IndexedDB has no projection
  // -- so this is one pass instead of N+2, not zero cost. Getting the audio out of
  // this store entirely (its own object store, keyed by segment key) is the
  // structural fix and needs a schema migration; this does not block it.
  //
  // The one metadata shape both scan paths below project. Kept in one place so the
  // cursor walk and the per-key fallback can never drift.
  const projectQueueRow = (key, r) => ({
    key,
    kind: r.kind,
    userId: r.userId,
    recordingId: r.recordingId,
    chatId: r.chatId,
    orgId: r.orgId,
    phase: r.phase,
    startedAt: r.startedAt,
    total: r.total,
    language: r.language,
    index: r.index,
    isFinal: !!r.isFinal,
    orphaned: !!r.orphaned,
    undecodable: !!r.undecodable,
    parked: r.parked,
    parkReason: r.parkReason,
    parkTarget: r.parkTarget,
    patientUserId: r.patientUserId,
    patientLabel: r.patientLabel,
    encounterUuid: r.encounterUuid,
    overrideIdentity: r.overrideIdentity === true,
    state: r.state,
    errorsCount: r.errorsCount,
    durationSecs: r.durationSecs,
    date: r.date,
  });

  const scanQueueMetadataByCursor = () =>
    new Promise((resolve, reject) => {
      const tx = dbRef.current.transaction(["recordings"], "readonly");
      const index = tx.objectStore("recordings").index("userId");
      const req = index.openCursor(IDBKeyRange.only(auth.user.id));
      const rows = [];
      req.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) return resolve(rows);
        rows.push(projectQueueRow(cursor.primaryKey, cursor.value || {}));
        cursor.continue();
      };
      req.onerror = (event) => reject(event.target?.error || event);
    });

  // The degraded path: keys first, then each row on its own, with a bad row SKIPPED
  // rather than fatal. A cursor walk fails as a whole — one row the engine cannot
  // deserialize rejects the entire scan, which aborted every pass and made the
  // recordings list reject too. The per-key read this replaced caught and skipped
  // that row; this keeps that property for the fallback. One readonly transaction
  // per key on purpose: an errored request aborts its transaction, which would take
  // every later read in the same one down with it. Result carries `partial = true`
  // when anything was skipped so the caller knows it is looking at an INCOMPLETE
  // view and must not delete on it.
  const scanQueueMetadataByKeys = async () => {
    const rows = [];
    let skipped = 0;
    const store = () => dbRef.current.transaction(["recordings"], "readonly").objectStore("recordings");
    const keys = await promisifyRequest(store().index("userId").getAllKeys(IDBKeyRange.only(auth.user.id)));
    for (const key of keys) {
      try {
        const r = await promisifyRequest(store().get(key));
        if (r) rows.push(projectQueueRow(key, r));
      } catch (e) {
        skipped++;
        if (!unreadableRowWarned.current) {
          unreadableRowWarned.current = true;
          console.error("Skipping an unreadable recording row during the queue scan", e?.name || "");
        }
      }
    }
    if (skipped > 0) rows.partial = true;
    return rows;
  };

  const scanQueueMetadata = async () => {
    try {
      return await scanQueueMetadataByCursor();
    } catch (e) {
      if (!scanFallbackWarned.current) {
        scanFallbackWarned.current = true;
        console.error("Recording queue cursor scan failed; reading rows one by one", e?.name || "");
      }
      return scanQueueMetadataByKeys();
    }
  };

  // Delete parked rows past PARKED_RECORDING_TTL_MS. Deliberately narrow: ONLY
  // `orphaned` and `undecodable`, the two states that mean this audio can never
  // reach a transcript however long it waits. A row that is merely FAILING -- an
  // outage, an encounter that has not synced yet, a spent give-up budget -- is
  // still audio somebody may get back, and age alone must never delete it.
  // Returns the keys removed so the caller can drop them from its snapshot.
  //
  // A segmented recording is purged as a WHOLE. The park flags are per row, but the
  // drain only ever flags the FINAL row of a segmented recording (its non-final
  // rows never reach the error path — Path A hands them off once a final exists),
  // so a per-row purge deleted 1 of N rows and stranded the rest as an unflagged
  // finalless set that was far past the 48h rebuild window: never rebuilt, never
  // purged, and (since the list started showing unfinished recordings) counted
  // toward the backlog alert forever. So: group segment rows by recordingId, treat
  // the group as parked if ANY of its rows is flagged (legacy rows already parked in
  // production carry the flag on one row only), age it by its NEWEST row, and delete
  // every key of the group. Whole-recording rows stay per-row — each is its own
  // recording. A group with no readable date is never purged: age is the only
  // thing that licenses this delete, and ambiguity must not.
  const purgeExpiredParkedRows = async (snapshot) => {
    const cutoff = Date.now() - PARKED_RECORDING_TTL_MS;
    const toMs = (d) => (d ? new Date(d).getTime() : NaN);
    const expiredKeys = [];
    const groups = new Map(); // recordingId -> { keys, parked, newest }
    for (const r of snapshot) {
      if (r.kind === "segment" && r.recordingId != null) {
        const g = groups.get(r.recordingId) || { keys: [], parked: false, newest: NaN };
        g.keys.push(r.key);
        if (r.orphaned || r.undecodable || r.parked === "identity") g.parked = true;
        const ts = toMs(r.date);
        if (Number.isFinite(ts) && !(g.newest >= ts)) g.newest = ts;
        groups.set(r.recordingId, g);
        continue;
      }
      if (!r.orphaned && !r.undecodable && r.parked !== "identity") continue;
      const ts = toMs(r.date);
      if (Number.isFinite(ts) && ts < cutoff) expiredKeys.push(r.key);
    }
    for (const g of groups.values()) {
      if (g.parked && Number.isFinite(g.newest) && g.newest < cutoff) expiredKeys.push(...g.keys);
    }
    if (!expiredKeys.length) return new Set();
    try {
      const tx = dbRef.current.transaction(["recordings"], "readwrite");
      const store = tx.objectStore("recordings");
      for (const key of expiredKeys) store.delete(key);
      await awaitTransaction(tx);
    } catch (e) {
      // Never fatal: the drain works fine with the rows still there.
      console.error("Failed to purge expired parked recordings", e?.name || "", e);
      return new Set();
    }
    console.log(
      `Purged ${expiredKeys.length} parked recording row(s) older than ${Math.round(PARKED_RECORDING_TTL_MS / 86400000)} days`
    );
    return new Set(expiredKeys);
  };

  // Fetch a single segment's stored audio (base64 data URL) by key.
  //
  // A row that is GONE is not an empty segment. Every caller got this key from a
  // walk of the store moments ago, so a missing row means the recording was
  // deleted underneath the assembly — Delete all in the backlog alert, Delete
  // older than N days in the modal, a sibling context that drained it. Returning
  // "" here read to the stitcher as "empty final fragment, not a loss": the MP3 was
  // built from whatever had been read before the delete landed, no counter moved,
  // and the drain uploaded a silently truncated transcript of audio the clinician
  // had just chosen to remove. Throw a tagged error instead, so the stitch counts
  // it and the assembler can refuse the upload.
  const getSegmentAudio = async (key) => {
    const tx = dbRef.current.transaction(["recordings"], "readonly");
    const row = await promisifyRequest(tx.objectStore("recordings").get(key));
    if (!row) {
      const gone = new Error("Queued segment row no longer exists");
      gone.segmentRowGone = true;
      throw gone;
    }
    return row.segmentAudio;
  };

  // Stitch a recording's stored segments into one 16kHz-mono MP3 and upload it via
  // parseRecording, then delete the consumed segment rows ON SUCCESS. This is the
  // ONE place the assemble -> upload -> delete sequence lives — called by BOTH the
  // drain's final-segment branch and Path A's finalless rebuild, so there is one
  // uploader, not two (spec §16.2). Always reuses the recording's ORIGINAL
  // recordingId so the server's M7 dedup collapses any drain / Path-A / cross-device
  // race into exactly one transcript. Throws on no-usable-audio (caller leaves the
  // rows queued), on a save error (caller classifies permanent vs transient), and
  // — tagged `recordingRowsGone` — when the rows were deleted while it was reading
  // them (caller: nothing to upload, nothing to retry, nothing to bump).
  //
  // GAP FILL: when fewer local rows survive than the recorder said it made (a
  // segment that never reached IndexedDB — quota, an aborted transaction, a session
  // that lapsed mid-recording), the missing indices are pulled back from the
  // server's A2 backstop copy BEFORE stitching. Previously that hole was simply
  // stitched over: the gapped MP3 was uploaded, one warning toast fired, and the
  // rows were deleted — leaving a clinical note permanently short a stretch of
  // audio that the server was holding all along.
  //
  // Returns { keptRows } — true when the assembly is still INCOMPLETE but the
  // missing audio is known to exist on the server (the byte pull failed). The
  // partial is uploaded so the clinician has the transcript now, the rows are KEPT,
  // and a later pass supersedes it with the complete recording. The caller must not
  // delete the row it holds when keptRows is true.
  const assembleAndUploadSegments = async ({
    recordingId,
    orderedSegs,
    chatId,
    phase,
    startedAt,
    expectedTotal,
    isRecovery = false,
    identity = null,
  }) => {
    // An offline encounter is captured under a browser-only temp id. Resolve its
    // durable server id before doing the expensive stitch or calling Transcript -
    // Save; if the encounter has not synced yet, throw and leave every audio row
    // queued for the existing backoff retry.
    const resolvedChatId = await resolveRecordingChatId(api.getChatsByTempId, brainId, chatId);

    const localSources = orderedSegs.map((s) => ({ kind: "local", key: s.key, index: s.index }));
    const localKeys = orderedSegs.map((s) => s.key);
    const missing = missingIndices(
      orderedSegs.map((s) => s.index),
      expectedTotal
    );
    let sources = localSources;
    let unfilled = missing;
    let rescuable = [];
    if (missing.length) {
      // Best-effort by contract: recordingGetSegments never throws — it returns an
      // empty, INCOMPLETE shape on failure, which reads here as "the server can fill
      // nothing" and falls straight through to the local-only assembly below.
      const { segments } = await api.recordingGetSegments(recordingId);
      const built = buildSegmentSources({ localSegs: orderedSegs, serverSegments: segments, expectedTotal });
      sources = built.sources;
      unfilled = built.unfilled;
      rescuable = built.rescuable;
      console.log(
        `Recording ${recordingId}: ${missing.length} segment(s) missing locally, ` +
          `${rescuable.length} recoverable from the server backstop`
      );
    }

    // A failed byte pull is CAPTURED, not thrown: the stitcher's own try/catch would
    // swallow a throw here (failedCount++/continue) and a falsy return reads as a
    // benign empty segment, so either would silently truncate the transcript — the
    // exact failure mode this whole path exists to prevent. Checked after the stitch,
    // before the result is used (same discipline as the cross-device pull).
    let pullError = null;
    // Same discipline for a LOCAL row that vanished mid-stitch: the recording was
    // deleted while this assembly was reading it (each read is its own transaction,
    // and a long recording's stitch runs for a minute or more). Latched for the
    // whole call, never per attempt — rows do not come back — and checked before
    // the upload, so what the clinician deleted never reaches the encounter as a
    // truncated transcript. Once set, no further rows are read.
    let rowsGone = false;
    const fetchSegmentAudio = async (src) => {
      if (rowsGone) return undefined;
      if (!src || src.kind === "local") {
        try {
          return await getSegmentAudio(src && src.key);
        } catch (e) {
          if (!e?.segmentRowGone) throw e;
          rowsGone = true;
          return undefined;
        }
      }
      if (pullError) return undefined; // already doomed — move no more PHI over the wire
      try {
        const r = await api.recordingGetSegmentBytes(recordingId, src.fileName);
        if (!r || !r.file) throw new Error("missing bytes for rescued segment " + src.index);
        return "data:" + (r.mimeType || src.mimeType || "audio/webm") + ";base64," + r.file;
      } catch (e) {
        pullError = e instanceof Error ? e : new Error(String(e));
        return undefined;
      }
    };

    const stitchOnce = (list) => {
      pullError = null; // per-attempt: stitchWithRetry may run this twice
      return stitchSegmentsStreaming({
        orderedKeys: list,
        fetchSegmentAudio,
        durationSecsEstimate: list.length * SEGMENT_SECS,
      });
    };

    let stitched = await stitchWithRetry(() => stitchOnce(sources), { attempts: 2 });
    // Latched: stitchOnce clears pullError per attempt, and the local-only fallback
    // below is itself an attempt — without this, a failed pull would look successful
    // by the time the retry decision is made.
    const gapPullFailed = pullError != null;
    if (gapPullFailed) {
      // The manifest listed the segment but its bytes never arrived, so the result
      // above may be silently short. Discard it and re-assemble from the local rows
      // alone — the pre-gap-fill behavior, never worse than it. Every gap counts as
      // unfilled again; the save below is marked partial and the rows are kept.
      console.warn("Segment gap fill failed; assembling from local segments only", pullError?.message || pullError);
      sources = localSources;
      unfilled = missing;
      stitched = await stitchWithRetry(() => stitchOnce(localSources), { attempts: 2 });
    }
    if (rowsGone) {
      // Deleted underneath us. Whatever was assembled from the rows read before the
      // delete landed is NOT this recording, and there is nothing left to retry
      // against — the caller treats this as "nothing to do", not as a failure.
      const gone = new Error("Recording rows were deleted during assembly");
      gone.recordingRowsGone = true;
      throw gone;
    }
    const { mp3DataUrl, decodedCount, failedCount } = stitched;
    if (!mp3DataUrl) {
      // No usable audio after retry — fail loud; leave queued for a later retry.
      const failure = new Error("Segmented recording could not be assembled");
      // Every segment was present and NONE decoded. Retrying re-reads the same bytes
      // and fails the same way: this is the 1.6K-event loop behind LUCASHEALTH-S5,
      // one recording re-attempted since July. Distinguished from a TRANSIENT stitch
      // failure (a dead encoder worker, memory pressure), which also decodes nothing
      // but reports no per-segment failures — that one deserves its retry.
      if (decodedCount === 0 && failedCount > 0) failure.undecodableAudio = true;
      throw failure;
    }

    // Is another attempt worth anything? Only when the audio still missing is audio
    // the SERVER HAS and we merely failed to fetch. A gap the backstop never
    // received can never improve, so that recording saves plain (today's behavior)
    // rather than lingering as a partial nothing will ever supersede.
    const keepForRetry = gapPullFailed && rescuable.length > 0 && !partialAttemptsExhausted(recordingId);

    // Upload the already-assembled MP3 directly (skip parseBlob's redundant
    // re-decode). startedAt stamps the transcript with the recording's REAL start
    // time; textTranscription + callback are unused (null / undefined). forcedPartial
    // is set ONLY when we intend to supersede this save: the backend then keeps it
    // replaceable and preserves the segment backstop for the complete retry.
    const saveOutcome = await parseRecording(
      mp3DataUrl,
      resolvedChatId,
      phase,
      undefined,
      null,
      recordingId,
      undefined,
      startedAt,
      keepForRetry,
      identity
    );
    // Encounter ids are recycled: a refusal means this audio belongs to some other
    // encounter. Nothing was uploaded and the rows must survive; the caller parks them.
    if (isIdentityRefusal(saveOutcome)) {
      const refused = new Error("Recording refused: it does not belong to this encounter");
      refused.identityRefusal = saveOutcome;
      throw refused;
    }
    // A resolve is NOT a save: parseRecording resolves with a sentinel when the
    // webview believes it is offline — this drain has no isOnline gate of its own,
    // so that refusal reaches here. Falling through would run the deletion below
    // and destroy the only copy of the audio with nothing uploaded. Throw a tagged
    // transient instead: both drain catches classify it as "reschedule, no
    // give-up strike, keep the rows" — connectivity is not corruption.
    if (saveOutcome === RECORDING_SAVE_DEFERRED_OFFLINE) {
      const deferred = new Error("Recording save deferred — webview believes it is offline");
      deferred.recordingSaveDeferredOffline = true;
      throw deferred;
    }
    // Warn only AFTER the save succeeded, so it fires once (coupled to the save) —
    // and only when this IS the final answer. A partial we are about to retry gets
    // its warning on the attempt that gives up, not on every attempt. presentCount
    // counts filled gaps as present, so a fully rescued recording never warns: the
    // audio is complete.
    if (!keepForRetry && shouldWarnPartialLoss({ decodedCount, failedCount, presentCount: sources.length, expectedTotal })) {
      toast.warning(t("hooks.useIndexDb.recordingPartiallyProcessed"));
    }
    if (keepForRetry) {
      const attempts = bumpPartialAttempts(recordingId);
      console.warn(
        `Recording ${recordingId} saved as a partial (${unfilled.length} segment(s) still missing); ` +
          `keeping local segments for a complete retry (attempt ${attempts}/${MAX_PARTIAL_ATTEMPTS})`
      );
      return { keptRows: true };
    }
    // Remember the acknowledged upload before its local evidence disappears.
    // Use the rows' capturing account, not whichever account is current after the
    // asynchronous save. A failed receipt write leaves later recovery fail-closed.
    const owner = orderedSegs[0];
    if (owner && orderedSegs.every((segment) => segment.userId === owner.userId && segment.orgId === owner.orgId)) {
      recordWebRecordingSaved(
        { recordingId, userId: owner.userId, orgId: owner.orgId },
        // A finalless capture cannot prove its tail survived. Known gaps or
        // decode failures likewise require review even after a partial upload.
        { complete: !isRecovery && expectedTotal > 0 && unfilled.length === 0 && failedCount === 0 }
      );
    }
    // Success -> delete ALL of this recording's LOCAL segment rows so they don't
    // linger or re-assemble (server-filled sources have no local row to delete).
    const delTx = dbRef.current.transaction(["recordings"], "readwrite");
    const delStore = delTx.objectStore("recordings");
    for (const segKey of localKeys) await promisifyRequest(delStore.delete(segKey));
    await awaitTransaction(delTx);
    clearPartialAttempts(recordingId);
    // Whichever path got this recording home, it is done: drop the pagehide marker
    // here rather than only on the Path A branch. Verified on staging — a tab closed
    // mid-recording survived long enough for the final flush, so the NORMAL path
    // assembled it and the marker was left behind. Harmless (the map is capped and
    // that recordingId is never consulted again) but it is litter, and litter in a
    // signal like this one is how the signal stops being trusted.
    clearCaptureInterrupted(recordingId);
    // Mark the recovery HERE, inside the shared primitive, so it lands on success
    // REGARDLESS of which Promise.race branch the caller observed — a slow finalless
    // recovery the caller saw "time out" must still suppress re-recovery when its
    // assemble eventually succeeds. The mark is silent (no UI announces a successful
    // recovery — the transcript on the encounter is the record); it only stops the
    // recovery surfaces from re-offering a recording that already saved. Off for the
    // normal final-segment save (nothing was recovered).
    if (isRecovery) markRecentlyRecovered(recordingId, resolvedChatId, startedAt);
    return { keptRows: false };
  };

  // Recordings that must never be surfaced as manageable — offered for deletion,
  // counted as backlog — because some tab may still be writing to them:
  //   - the capture running in THIS tab (the in-memory active marker);
  //   - any recording with a heartbeat younger than STALE_REBUILD_MS;
  //   - any UNFINISHED segment set (no final row) whose newest row is younger than
  //     STALE_REBUILD_MS.
  // STALE_REBUILD_MS (120s), not the 30s heartbeat window this used to apply: a
  // heartbeat is written only on segment flush, and a background tab's timers are
  // clamped to ~60s, so a capture running in another tab — or the Chrome side
  // panel, same origin, same database — went "dead" for this list after 30s and was
  // offered for deletion while it was still being recorded. The drain's own
  // finalless gate already treats 120s of segment recency as the authority
  // (recordingRebuildGate.js); the list now uses the same rule, so what the drain
  // will not touch the clinician is not invited to delete. Computed per distinct
  // recordingId, with ONE read of the heartbeat map — not one per row.
  const liveRecordingIds = (rows) => {
    const live = new Set();
    const active = getActiveRecording();
    if (active) live.add(active);
    const ids = new Set();
    const hasFinal = new Set();
    const newestSegmentMs = new Map();
    for (const r of rows || []) {
      if (!r || !r.recordingId) continue;
      ids.add(r.recordingId);
      if (r.kind !== "segment") continue;
      if (r.isFinal) hasFinal.add(r.recordingId);
      const ts = r.date ? new Date(r.date).getTime() : NaN;
      if (Number.isFinite(ts) && !(newestSegmentMs.get(r.recordingId) >= ts)) newestSegmentMs.set(r.recordingId, ts);
    }
    const fresh = freshHeartbeatIds(STALE_REBUILD_MS);
    const now = Date.now();
    for (const id of ids) {
      if (live.has(id)) continue;
      if (fresh.has(id)) {
        live.add(id);
        continue;
      }
      // A whole-recording row is written when the capture STOPS, so only a segment
      // set can still be growing — and only one whose final row has not landed.
      if (newestSegmentMs.has(id) && !hasFinal.has(id) && now - newestSegmentMs.get(id) <= STALE_REBUILD_MS) {
        live.add(id);
      }
    }
    return Array.from(live);
  };

  // Read the queue as lightweight metadata (NO audio: blobData/segmentAudio are
  // omitted so the modal never pulls ArrayBuffers into memory), then group into
  // logical recordings for display. Mirrors getSegmentKeysForRecording's
  // cursor-based, memory-bounded scan.
  const listRecordings = async () => {
    if (!auth?.user?.id) return [];
    if (!(await ensureDb())) return [];
    const rows = await scanQueueMetadata();
    return groupRecordings(rows, {
      warnAfterErrors: WARN_AFTER_ERRORS,
      segmentSecs: SEGMENT_SECS,
      // Unfinished (finalless) recordings are the ones a clinician most needs to
      // see: a tab that slept through a capture leaves hours of audio that the
      // drain can only rescue through the finalless path, and while they were
      // excluded from this list there was NOTHING in the UI that could count,
      // save or delete them — an entirely-finalless queue rendered as "No saved
      // recordings" with hours behind it.
      includeFinalless: true,
      // The one that must stay hidden is the capture happening right now, whose
      // segments are still arriving. That is an identity check: this browser's
      // active marker, plus any recording whose cross-tab heartbeat is still warm
      // (a sibling tab may own it).
      excludeRecordingIds: liveRecordingIds(rows),
    });
  };

  // Refresh the React-state snapshot the modal/badge render from.
  const refreshRecordings = async () => {
    try {
      const list = await listRecordings();
      setRecordings((prev) => (sameRecordingList(prev, list) ? prev : list));
    } catch (e) {
      console.error("Error listing recordings", e);
    }
  };

  // Hand a queued recording's audio to the clinician as a file. The last backstop:
  // when the queue cannot get a recording to the encounter — a deleted encounter, a
  // save the backend keeps refusing — the audio is still here, and walking away with
  // it beats watching it be deleted. Reads only; the row is untouched, so a later
  // retry (or the drain finally succeeding) still works.
  //
  // `sink(blob, fileName)` is where the file goes: by default the anchor download
  // (saveAudioFile), whose true means "handed to the browser". A bulk save passes a
  // sink that writes into a folder the clinician picked and resolves true only once
  // the bytes are on disk — Save all is offered right before Delete all, and its
  // count has to be one it can stand behind. The sink's boolean is returned as-is.
  //
  // Returns true when the sink reported the file handed over. Never throws: every
  // caller is already on a failure path and needs to be able to say it did not
  // happen.
  const exportRecording = async (keys, { patient, sink = saveAudioFile } = {}) => {
    if (!keys || keys.length === 0 || !(await ensureDb())) return false;
    try {
      const tx = dbRef.current.transaction(["recordings"], "readonly");
      const store = tx.objectStore("recordings");
      const rows = [];
      for (const key of keys) {
        const row = await promisifyRequest(store.get(key));
        if (row) rows.push({ key, row });
      }
      if (rows.length === 0) return false;

      const first = rows[0].row;
      let blob = null;
      let mimeType = "";

      if (first.kind === "segment") {
        // Same streaming assembly the drain uses, so a long recording never holds
        // more than one decoded segment plus the growing MP3 in memory. Local rows
        // only: this is an escape hatch, not a recovery — it must work offline.
        const ordered = rows
          .map(({ key, row }) => ({ key, index: typeof row.index === "number" ? row.index : 0 }))
          .sort((a, b) => a.index - b.index);
        const { mp3DataUrl, failedCount } = await stitchWithRetry(
          () =>
            stitchSegmentsStreaming({
              orderedKeys: ordered.map((s) => s.key),
              fetchSegmentAudio: getSegmentAudio,
              durationSecsEstimate: ordered.length * SEGMENT_SECS,
            }),
          { attempts: 2 }
        );
        // The stitcher is lenient by design — a segment that will not read or
        // decode is skipped and COUNTED, and the MP3 is still produced from the
        // rest — because the drain that shares it warns the clinician about the
        // shortfall after the upload. This path has no such warning: its true is
        // the bulk save's "Saved N", said right before Delete all. A file missing
        // any segment is not that recording, so it is not handed over and not
        // counted; the rows stay, and the drain (which does warn) still has them.
        if (mp3DataUrl && failedCount > 0) {
          console.error(
            `Queued recording export dropped ${failedCount} of ${ordered.length} segment(s); not handing over a truncated file`
          );
          return false;
        }
        blob = dataUrlToBlob(mp3DataUrl);
        mimeType = "audio/mpeg";
      } else if (first.blobData && first.blobType) {
        blob = new Blob([first.blobData], { type: first.blobType });
        mimeType = first.blobType;
      } else if (first.blob) {
        blob = first.blob; // legacy rows stored the Blob directly
        mimeType = first.blob.type;
      }

      if (!blob) {
        console.error("Could not assemble the queued recording for export");
        return false;
      }
      const fileName = audioFileName({ patient, dateMs: first.date ? new Date(first.date).getTime() : 0, mimeType });
      return !!(await sink(blob, fileName));
    } catch (e) {
      console.error("Error exporting queued recording", e?.name || "", e);
      return false;
    }
  };

  // Permanently remove one or more logical recordings by primary key. This is the
  // ONLY deletion path — the modal and the backlog alert gate it behind an explicit
  // confirm. Audio cannot be recovered.
  //
  // `keyLists` is one key array per logical recording (`rows.map(r => r.keys)`: a
  // whole-recording row is one key, a segmented recording is all of its rows); a
  // flat array of keys is accepted too, each key counted as its own recording. ONE
  // readwrite transaction for the lot and ONE list refresh afterwards: the bulk
  // actions used to call the single-recording delete in a loop, which meant one
  // transaction plus a full cursor walk of the queue plus a re-render PER recording
  // — thousands of walks for the queue this exists for — and, because that delete
  // swallowed its own failure, a success toast whether or not anything was removed.
  //
  // Resolves { ok, deleted, failed } and NEVER throws: `deleted`/`failed` count
  // logical recordings, and a transaction that aborts rolls every delete back, so
  // failure is all-or-nothing — ok:false means nothing was removed.
  const deleteRecordings = async (keyLists) => {
    const lists = [];
    for (const item of keyLists || []) {
      if (Array.isArray(item)) lists.push(item.filter((k) => k != null));
      else if (item != null) lists.push([item]);
    }
    const keys = lists.flat();
    if (keys.length === 0) return { ok: true, deleted: 0, failed: 0 };
    if (!(await ensureDb())) {
      console.error("Database not initialized, while deleting recordings");
      return { ok: false, deleted: 0, failed: lists.length };
    }
    let ok = true;
    try {
      const tx = dbRef.current.transaction(["recordings"], "readwrite");
      const store = tx.objectStore("recordings");
      // Fire every delete, then wait for the COMMIT — not for each request in turn.
      for (const key of keys) store.delete(key);
      await awaitTransaction(tx);
    } catch (e) {
      ok = false;
      console.error("Error deleting recordings", e?.name || "");
    }
    // A deletion changes what's pending — let the next drain re-announce if needed.
    summaryShown.current = false;
    await refreshRecordings();
    return ok ? { ok: true, deleted: lists.length, failed: 0 } : { ok: false, deleted: 0, failed: lists.length };
  };

  // Single-recording form of deleteRecordings. Resolves the boolean `ok`.
  const deleteRecording = async (keys) => {
    if (!keys || keys.length === 0) return false;
    const { ok } = await deleteRecordings([keys]);
    return ok;
  };

  // Force an immediate upload attempt. Clears the backoff and the failure counters
  // on the targeted rows (so they show as Pending again and aren't suppressed),
  // then drains. An identity-parked row (its encounter id now belongs to another
  // encounter) is only released by `overrideIdentity`, the modal's explicit
  // "Attach anyway"; the flag rides on the row for ONE attempt.
  const retryRecording = async (keys, { overrideIdentity = false } = {}) => {
    if (!keys || keys.length === 0 || !(await ensureDb())) return;
    try {
      const tx = dbRef.current.transaction(["recordings"], "readwrite");
      const store = tx.objectStore("recordings");
      for (const key of keys) {
        const row = await promisifyRequest(store.get(key));
        if (row) {
          // An explicit user Retry overrides the automated give-up + in-session
          // permanent-skip AND the orphaned (deleted-encounter) park, so an
          // abandoned recovery isn't permanently stuck.
          clearGiveUp(row.recordingId);
          recoveryPermSkip.current.delete(row.recordingId);
          const unpark = overrideIdentity
            ? { parked: undefined, parkReason: undefined, parkTarget: undefined, overrideIdentity: true }
            : {};
          await promisifyRequest(
            store.put({ ...row, errorsCount: 0, state: "error", orphaned: false, undecodable: false, ...unpark }, key)
          );
        }
      }
      await awaitTransaction(tx);
    } catch (e) {
      console.error("Error resetting recording for retry", e);
    }
    retryAttempt.current = 0;
    summaryShown.current = false;
    await refreshRecordings();
    checkRecordings();
  };

  // Force an immediate drain of the whole queue (no per-row reset). An explicit
  // "Retry all" also clears the in-session permanent-skip set so signed-encounter
  // skips from this session don't survive a deliberate retry.
  const retryAll = async () => {
    retryAttempt.current = 0;
    summaryShown.current = false;
    recoveryPermSkip.current.clear();
    checkRecordings();
  };

  // What the identity guard in parseRecording needs from a queued row. Rows queued
  // before patient capture carry no patientUserId and are judged on time alone; rows
  // queued before the encounter UUID existed carry no encounterUuid and are judged on
  // time and patient.
  const drainIdentity = (recording) => ({
    deferred: true,
    patientUserId: recording.patientUserId,
    encounterUuid: recording.encounterUuid,
    overrideIdentity: recording.overrideIdentity === true,
    sourceChatId: recording.chatId,
  });

  // Park a refused recording: kept, listed, never auto-retried, no error strike.
  // A segmented recording parks EVERY row (same reason as the orphan park below).
  const parkIdentityRefusal = async (key, recording, refusal) => {
    const target = refusal?.chat || {};
    const parkTarget = {
      chatId: target.id ?? null,
      patientLabel: target.patientLabel || "",
      createdOn: target.createdOn ?? null,
    };
    const park = (row) => ({
      ...row,
      state: "error",
      parked: "identity",
      parkReason: refusal?.reason || null,
      parkTarget,
      overrideIdentity: false,
    });
    await new Promise((resolve, reject) => {
      const tx = dbRef.current.transaction(["recordings"], "readwrite");
      const store = tx.objectStore("recordings");
      if (recording.kind === "segment" && recording.recordingId) {
        const req = store.index("userId").openCursor(IDBKeyRange.only(auth.user.id));
        req.onsuccess = (event) => {
          const cursor = event.target.result;
          if (!cursor) return;
          const row = cursor.value;
          if (row && row.kind === "segment" && row.recordingId === recording.recordingId) cursor.update(park(row));
          cursor.continue();
        };
        req.onerror = (event) => reject(event.target?.error || event);
      } else {
        const req = store.get(key);
        req.onsuccess = () => {
          if (req.result) store.put(park(req.result), key);
        };
        req.onerror = (event) => reject(event.target?.error || event);
      }
      tx.oncomplete = () => resolve();
      tx.onabort = (event) => reject(tx.error || event.target?.error || event);
      tx.onerror = (event) => reject(tx.error || event.target?.error || event);
    });
    console.warn("Queued recording does not belong to its encounter; parked (audio kept, no more automatic retries)", {
      recordingId: recording.recordingId,
      reason: refusal?.reason,
    });
    if (!identityParkWarned.current) {
      identityParkWarned.current = true;
      toast.warning(t("hooks.useIndexDb.recordingIdentityParked"));
    }
  };

  const checkRecordings = async () => {
    // The scheduled retry, the online/visibilitychange listeners, and the
    // auth/db effect can all fire near-simultaneously. Without this guard two
    // runs read the same not-yet-deleted recording and upload it twice, which
    // duplicates the transcript on the encounter. One sync at a time.
    if (syncing.current) {
      // A trigger (reconnect/visibility/timer) fired mid-run. Remember it and
      // re-drain when this pass ends, so recovery isn't delayed a full backoff
      // interval when connectivity returns during an in-flight sync.
      rescanRequested.current = true;
      return;
    }
    syncing.current = true;
    try {
      // A pass that outlives the provider must go quiet, not just stop scheduling
      // (see the unmount cleanup): a segment write fires this pass and nothing
      // awaits it, so it can resume after any `await` below against a dead
      // component. Every console call it makes after a Jest file has finished
      // fails the whole run with all suites green (CI #1522: 766/766 passing,
      // exit code 1). Hence the `alive` checks at each resume point.
      if (!alive.current) return;
      console.log("Checking for unsynced recordings...");
      if (!auth?.user?.id) return;
      if (!(await ensureDb())) {
        // No database yet — an open blocked by another context holding an older
        // version clears itself once that context goes away. Come back rather than
        // stranding the queue for the rest of the session.
        retryCheck();
        return;
      }

      // ONE metadata pass over the queue (no audio) drives every decision below.
      // Reading each row in FULL up front is what made a deep queue quadratic —
      // see scanQueueMetadata. The full row is fetched only where it is genuinely
      // needed: the whole-recording (blob) leg, and the read-modify-write in the
      // error path.
      let queue = await scanQueueMetadata();
      if (!alive.current) return;
      // The scan had to skip rows it could not read (see scanQueueMetadataByKeys).
      // What follows still drains what IS readable, but nothing may be DELETED on
      // the strength of an incomplete view, and neither the depth report nor the
      // backlog question can be answered from one. Captured before the filter
      // below, which returns a fresh array without the marker.
      const partialScan = !!queue.partial;

      // Parked rows that can never sync are the reason a queue grows without
      // bound. Drop the expired ones before anything else so the pass below (and
      // the modal snapshot at the end) never sees them.
      if (!partialScan) {
        const purgedKeys = await purgeExpiredParkedRows(queue);
        if (purgedKeys.size) queue = queue.filter((r) => !purgedKeys.has(r.key));
      }

      if (!alive.current) return;
      if (queue.length > 0) {
        console.log("Found " + queue.length + " unsynced recordings" + (partialScan ? " (partial scan)" : ""));
      }
      // A queue this deep is a fault, not a backlog, and the console.log above is
      // invisible to Sentry (captureConsoleIntegration collects errors only) — which
      // is exactly why 4728 rows went unnoticed for weeks. Once per session.
      if (!partialScan && queue.length > QUEUE_DEPTH_WARN_ROWS && !queueDepthWarned.current) {
        queueDepthWarned.current = true;
        const parked = queue.filter((r) => r.orphaned || r.undecodable || r.parked === "identity").length;
        // The cursor walks the userId index, which says nothing about date order.
        const oldest = queue.reduce((min, r) => {
          const ts = r.date ? new Date(r.date).getTime() : NaN;
          return Number.isFinite(ts) && (min === null || ts < min) ? ts : min;
        }, null);
        console.error(
          "Recording queue is not draining — " +
            queue.length +
            " rows queued (" +
            parked +
            " parked); the oldest is from " +
            (oldest === null ? "unknown" : new Date(oldest).toISOString())
        );
      }

      let madeProgress = false;
      let anyPending = false;
      // Path A: evaluate each finalless recording at most once per drain pass (a
      // recording spans several segment-key rows; without this the same set would
      // be re-evaluated — a full cursor walk each time — for every one of its keys).
      const rebuiltThisPass = new Set();
      // Recordings whose rows this pass has already consumed (assembled + deleted).
      // Replaces the "removed by a concurrent pass" check that the per-key full read
      // used to give us for free.
      const consumedRecordings = new Set();
      // Parked RECORDINGS, from the per-row flags. The error path now flags every
      // row of a segmented recording it parks, but rows parked before it did carry
      // the flag on the final row only — and the non-final rows of such a recording
      // were still walked every pass (each one a Path A candidate, each a cursor
      // walk), still counted, and still eligible for a rebuild the flag was meant to
      // stop. Whatever row carries the flag, the whole recording is parked.
      const parkedRecordingIds = new Set();
      for (const r of queue) {
        if (r.recordingId && (r.orphaned || r.undecodable || r.parked === "identity")) parkedRecordingIds.add(r.recordingId);
      }

      for (let i = 0; i < queue.length; i++) {
        if (!alive.current) return;
        const recording = queue[i];
        const key = recording.key;
        // Confirmed-orphaned rows (their encounter was DELETED — see the
        // classification in the catch below) are parked: never auto-retried
        // (each retry was a guaranteed 404, forever), NOT counted as pending
        // (so an all-orphaned queue stops reschedule churn), still listed in
        // the Recordings modal where Retry (clears the flag) and Delete work.
        if (recording.orphaned) continue;
        // Same park, different cause: these bytes will never decode.
        if (recording.undecodable) continue;
        // Same park again: this audio does not belong to the encounter its id now
        // names (recycled id). Only an explicit "Attach anyway" releases it.
        if (recording.parked === "identity") continue;
        // Any row of a parked recording, flagged or not (see parkedRecordingIds).
        if (recording.recordingId && parkedRecordingIds.has(recording.recordingId)) continue;
        // Its recording was assembled and its rows deleted earlier in this very
        // pass, so this key no longer exists.
        if (recording.recordingId && consumedRecordings.has(recording.recordingId)) continue;
        try {
          // The work for this entry: a streamed SEGMENT stitches all stored audio
          // on the final piece and transcribes ONCE via the working parseBlob path;
          // a whole recording reconstructs its blob and runs the parseBlob path.
          let work;
          let workTimeoutMs = PARSE_TIMEOUT_MS; // scaled up for long segmented recoveries (see below)
          if (recording.kind === "segment") {
            if (!recording.isFinal) {
              // 2b-2 Path A: a CRASHED / abandoned finalless set (no final segment).
              // The drain skips these by default; when auto-recover is enabled, rebuild
              // from THIS browser's own IndexedDB copy (most complete, same codec).
              // SELF-CONTAINED (own timeout + try/catch, then `continue`) so it never
              // reaches the shared single-key delete below — a finalless set is deleted
              // all-or-nothing inside assembleAndUploadSegments, never one row at a time
              // (otherwise a swallowed permanent error would truncate the kept audio).
              const recoverEnabled = isRecordingAutoRecoverEnabledFromStorage();
              const skipEnv =
                (typeof window !== "undefined" && !!window.ReactNativeWebView) || !!(isDemoRef && isDemoRef.current);
              if (!recoverEnabled || skipEnv) {
                // Kill-switch off / native webview (re-encode OOMs) / demo (never
                // touch real PHI) → legacy behavior: leave the set queued, untouched.
                continue;
              }
              if (recoveryPermSkip.current.has(recording.recordingId)) continue; // signed/archived this session
              if (getGiveUp(recording.recordingId) >= MAX_RECOVERY_GIVEUP) continue; // repeatedly failed — stop
              // One Path A evaluation per recording per pass — marked BEFORE the
              // cursor walk, whatever it finds. Marking only a set that turned out to
              // be finalless meant every non-final row of a recording whose final row
              // was failing (or parked) paid a full walk of the user's queue, on every
              // pass, for a set the else-branch below owns anyway.
              if (rebuiltThisPass.has(recording.recordingId)) continue;
              rebuiltThisPass.add(recording.recordingId);

              const segs = await getSegmentKeysForRecording(recording.recordingId);
              // Its rows are gone — deleted from the modal, or drained by a sibling
              // context, since the snapshot was taken. Nothing to rebuild.
              if (segs.length === 0) continue;
              // If ANY segment is final this is NOT a finalless set — the else branch
              // (final-segment path) owns it; don't double-handle.
              if (segs.some((s) => s.isFinal)) continue;

              // Backgrounding can suspend the recorder's heartbeat without
              // releasing its browser-owned lock. Never assemble/delete those
              // segments while their live owner can still deliver more audio.
              // A failed ownership inspection also keeps the set intact.
              if ((await hasLiveWebRecordingOwner(recording.recordingId)) !== false) {
                anyPending = true;
                retryCheck();
                continue;
              }

              const now = Date.now();
              const dates = segs.map((s) => s.date || 0).filter(Boolean);
              const newestSegmentAge = now - (dates.length ? Math.max(...dates) : 0);
              const oldestSegmentAge = dates.length ? now - Math.min(...dates) : 0;
              const lockHeld = claimRecoveryLock(recording.recordingId, RECOVERY_LOCK_TTL_MS);
              // 3×SEGMENT (~30s) heartbeat window; BLOCK-only. The ~120s
              // segment-recency in the gate is the load-bearing live guard.
              const heartbeatFresh = isHeartbeatFresh(recording.recordingId, 3 * SEGMENT_SECS * 1000);
              const isActive = getActiveRecording() === recording.recordingId;
              // The capture context said it was being destroyed (useRecorder's
              // pagehide). Then this set is dead, not young, and the 120s inference
              // is pure delay.
              const interrupted = wasCaptureInterrupted(recording.recordingId);
              const eligible = shouldRebuildFinalless({
                enabled: true,
                orgMatch: !!recording.orgId && recording.orgId === currentOrgId(),
                newestSegmentAge,
                oldestSegmentAge,
                heartbeatFresh,
                isActive,
                lockHeld,
                interrupted,
              });
              if (!eligible) {
                if (lockHeld) releaseRecoveryLock(recording.recordingId);
                // When TIME is the only thing holding it back, come back for it. The
                // skip used to be silent and armed nothing, so a set that qualified a
                // minute later waited for an unrelated event — a tab focus, another
                // recording, a reload — to be noticed at all.
                const waitMs = msUntilRebuildEligible({ newestSegmentAge, heartbeatFresh, isActive, interrupted });
                if (waitMs !== null && waitMs > 0) scheduleEligibilityRecheck(waitMs);
                continue; // too recent / live / locked elsewhere
              }

              // Eligible. expectedTotal = max(index)+1 so an INTERIOR gap is filled
              // from the server backstop (and warned about if it cannot be); trailing
              // loss without a final is the accepted residual (spec §14C). Same
              // PARSE_TIMEOUT bound as the drain.
              const maxIndex = segs.reduce((m, s) => Math.max(m, s.index), -1);
              let rebuildTimer;
              try {
                const rebuildOutcome = await Promise.race([
                  assembleAndUploadSegments({
                    recordingId: recording.recordingId,
                    orderedSegs: segs,
                    chatId: recording.chatId,
                    phase: recording.phase,
                    startedAt: recording.startedAt,
                    expectedTotal: maxIndex + 1,
                    // Write the consent notice INSIDE the primitive so a slow recovery
                    // that wins the timeout race still surfaces it on eventual success.
                    isRecovery: true,
                    identity: drainIdentity(recording),
                  }),
                  new Promise((_, rejectRace) => {
                    rebuildTimer = setTimeout(
                      () => {
                        const e = new Error("recovery rebuild timed out");
                        e.isTimeout = true;
                        rejectRace(e);
                      },
                      recoveryTimeoutMs(segs.length, PARSE_TIMEOUT_MS)
                    );
                  }),
                ]);
                releaseRecoveryLock(recording.recordingId);
                clearCaptureInterrupted(recording.recordingId);
                if (rebuildOutcome && rebuildOutcome.keptRows) {
                  // Saved as a supersed-able partial: the audio still missing lives on
                  // the server and its pull failed, so the rows stay for a later pass.
                  // Not progress — nothing left the queue.
                  anyPending = true;
                  retryCheck();
                } else {
                  // Success: the segment rows were deleted AND the "Recovered — Undo"
                  // consent notice was written inside the shared uploader (§15.1).
                  madeProgress = true;
                  // Its other rows are gone; the snapshot this pass is walking still
                  // lists them.
                  if (recording.recordingId) consumedRecordings.add(recording.recordingId);
                }
              } catch (saveErr) {
                releaseRecoveryLock(recording.recordingId);
                if (saveErr?.identityRefusal) {
                  try {
                    await parkIdentityRefusal(key, recording, saveErr.identityRefusal);
                  } catch (parkErr) {
                    console.error("Could not park a refused recording", parkErr?.name || "");
                  }
                  if (recording.recordingId) parkedRecordingIds.add(recording.recordingId);
                  continue;
                }
                if (saveErr?.recordingRowsGone) {
                  // Deleted while the rebuild was reading it (the modal, a sibling
                  // context). Nothing was uploaded and nothing is left to retry:
                  // not pending, not a give-up strike, and the rest of this
                  // snapshot's rows for it are stale.
                  if (recording.recordingId) consumedRecordings.add(recording.recordingId);
                  console.log("Finalless recording was deleted during its rebuild; nothing uploaded");
                  continue;
                }
                anyPending = true;
                if (saveErr?.serverUnavailable) {
                  // Server unreachable — the upload never left the device, so this
                  // says NOTHING about audio integrity. Keep the segment rows queued
                  // with no give-up strike and deliberately no exhaustion ceiling
                  // (an outage may outlast any budget); the next tick retries.
                  retryCheck();
                } else if (isPermanentRecoveryError(saveErr)) {
                  // Encounter signed/archived before this drained — it can never be
                  // attached. Stop retrying + tell the clinician ONCE; KEEP the audio
                  // (the uploader deletes only on success, so the rows survive).
                  recoveryPermSkip.current.add(recording.recordingId);
                  toast.info(t("hooks.useIndexDb.recoveredCouldNotAttachSigned"));
                } else if (
                  saveErr &&
                  saveErr.chatIdResolutionPending &&
                  !saveErr.sessionExpired &&
                  !chatIdResolutionExhausted(recording)
                ) {
                  // The offline encounter has not synced yet. This says nothing about
                  // audio integrity, so keep automatic retries alive without consuming
                  // the finalless corrupt-audio give-up budget — UNLESS the row has
                  // waited past the resolution ceiling (a never-syncing draft / bad org
                  // data) or the failure is a session expiry. Both of those fall through
                  // to the bounded give-up path below so resolution can't retry forever.
                  retryCheck();
                  console.log("Offline encounter mapping not ready; rescheduling recording recovery");
                } else if (saveErr && saveErr.isTimeout) {
                  // A slow assemble/upload (a LONG recording on a slow link) is NOT a
                  // corrupt recording — do NOT count it toward give-up, or a legitimate
                  // 30-min recovery gets permanently abandoned after 3 slow attempts.
                  // Reschedule; the length-scaled budget gives the retry more room.
                  retryCheck();
                  console.warn("Finalless recovery timed out; rescheduling (not a give-up strike)");
                } else if (saveErr && saveErr.recordingSaveDeferredOffline) {
                  // The webview believes it is offline — the save was refused, not
                  // failed, and says nothing about audio integrity. No give-up
                  // strike; the rows survived (the uploader deletes only on
                  // success) and the reschedule retries once connectivity returns.
                  retryCheck();
                  console.log("Recording save deferred (webview offline); rescheduling recovery");
                } else {
                  // Transient (incl. no-usable-audio) — bound the retries so a
                  // permanently-corrupt finalless set can't re-pull forever.
                  bumpGiveUp(recording.recordingId);
                  // Log ONLY message + status — never the raw axios error, whose
                  // config.data/response.data carry the base64 clinical audio + ids
                  // (Sentry's captureConsoleIntegration would lift that into the event,
                  // and beforeSend doesn't scrub custom contexts). Mirrors
                  // useCrossDeviceRecovery.js's recover-failed log.
                  console.error(
                    "Finalless recovery failed; will retry until give-up —",
                    (saveErr && saveErr.message) || saveErr,
                    saveErr && saveErr.response && saveErr.response.status ? "(HTTP " + saveErr.response.status + ")" : ""
                  );
                }
              } finally {
                clearTimeout(rebuildTimer);
              }
              continue; // Path A fully handled — never touch the shared delete-key path
            } else {
              // Final segment: assemble + upload via the SAME shared primitive Path A
              // uses (one uploader, spec §16.2), then fall through to the shared
              // Promise.race timeout + delete-key cleanup below. Behavior unchanged.
              const ordered = await getSegmentKeysForRecording(recording.recordingId);
              // Deleted mid-pass (the modal, or a sibling context that drained it):
              // there is nothing local to assemble, and the gap fill would otherwise
              // rebuild the whole recording from the server backstop — resurrecting
              // audio the clinician just chose to remove. (A delete that lands
              // AFTER this walk, mid-stitch, is caught inside the assembler.)
              if (ordered.length === 0) continue;
              const expectedTotal = recording.total || ordered.length;
              // A long completed recording also needs a length-scaled budget so the
              // shared race below doesn't time out mid-assemble and re-do the whole
              // stitch every backoff tick.
              workTimeoutMs = recoveryTimeoutMs(ordered.length, PARSE_TIMEOUT_MS);
              work = assembleAndUploadSegments({
                recordingId: recording.recordingId,
                orderedSegs: ordered,
                chatId: recording.chatId,
                phase: recording.phase,
                startedAt: recording.startedAt,
                expectedTotal,
                identity: drainIdentity(recording),
              });
            }
          } else {
            const resolvedChatId = await resolveRecordingChatId(api.getChatsByTempId, brainId, recording.chatId);
            // The ONLY leg that needs the audio itself, so this is the one place the
            // full row is read — and the row may have been drained by a concurrent
            // pass since the snapshot was taken, which the old per-key read used to
            // catch for free.
            const full = await promisifyRequest(
              dbRef.current.transaction(["recordings"], "readonly").objectStore("recordings").get(key)
            );
            if (!full) continue; // removed by a concurrent pass
            // Convert ArrayBuffer back to Blob
            let blob;
            if (full.blobData && full.blobType) {
              // New format: ArrayBuffer stored
              console.log(
                "Reconstructing blob from ArrayBuffer for recording " +
                  recording.date.getTime() +
                  " for chat " +
                  recording.chatId +
                  " type: " +
                  full.blobType
              );
              blob = new Blob([full.blobData], { type: full.blobType });
            } else if (full.blob) {
              // Old format: Blob stored directly (fallback for existing data)
              console.log("Using existing blob for recording " + recording.date.getTime() + " for chat " + recording.chatId);
              blob = full.blob;
            } else {
              throw new Error("No blob data found in recording");
            }
            // Whole-recording rows queued before startedAt was stored are judged by
            // their queue date, which is never earlier than the real start.
            const blobStartedAt =
              typeof recording.startedAt === "number"
                ? recording.startedAt
                : recording.date
                  ? new Date(recording.date).getTime()
                  : undefined;
            work = parseBlob(blob, resolvedChatId, recording.phase, full.extraData, undefined, recording.recordingId, undefined, {
              background: true,
              identity: { ...drainIdentity(recording), startedAt: blobStartedAt },
            });
          }

          let parseTimer;
          let workOutcome;
          try {
            workOutcome = await Promise.race([
              work,
              new Promise((_, rejectRace) => {
                parseTimer = setTimeout(() => rejectRace(new Error("recording upload timed out")), workTimeoutMs);
              }),
            ]);
          } finally {
            // Clear on BOTH resolve and reject — the reject path skips a trailing
            // statement, which would otherwise leak a 3-min timer per failed retry.
            clearTimeout(parseTimer);
          }

          // The blob leg resolves THROUGH parseBlob, which forwards parseRecording's
          // outcome: a webview that believes it is offline REFUSES the save and
          // resolves with a sentinel instead of throwing. That is not a success —
          // deleting the row below would destroy the only copy of the audio with
          // nothing uploaded. Keep the row queued and reschedule, penalty-free.
          // (The segment legs throw a tagged error for the same case, handled in
          // the catch below.)
          if (workOutcome === RECORDING_SAVE_DEFERRED_OFFLINE) {
            anyPending = true;
            retryCheck();
            console.log("Recording save deferred (webview offline); keeping row queued");
            continue;
          }
          // The blob leg resolves a refusal the same way; route it to the park below.
          if (isIdentityRefusal(workOutcome)) {
            const refused = new Error("Recording refused: it does not belong to this encounter");
            refused.identityRefusal = workOutcome;
            throw refused;
          }

          // An INCOMPLETE assembly whose missing audio is known to sit on the server:
          // the partial is already uploaded, and assembleAndUploadSegments kept every
          // segment row (this one included) so a later pass can pull the gap and
          // supersede it with the complete recording. Deleting the key here would
          // destroy exactly the audio the retry needs.
          if (workOutcome && workOutcome.keptRows) {
            anyPending = true;
            retryCheck();
            console.log("Recording saved as a partial; segment rows kept for a complete retry");
            continue;
          }

          if (recording.kind !== "segment") {
            recordWebRecordingSaved({
              recordingId: recording.recordingId,
              userId: recording.userId,
              orgId: recording.orgId,
            });
          }
          // Success -> delete the entry in a fresh readwrite transaction
          // (segment rows were already deleted inside the work closure above;
          // this delete is a no-op for the final-segment key — IndexedDB
          // silently ignores deletes of already-removed keys).
          const delTx = dbRef.current.transaction(["recordings"], "readwrite");
          const delStore = delTx.objectStore("recordings");
          const deleted = awaitTransaction(delTx);
          delStore.delete(key);
          await deleted;
          madeProgress = true;
          // Same as Path A: a segmented recording's other rows were deleted inside
          // the work closure, so the rest of this snapshot's entries for it are stale.
          if (recording.kind === "segment" && recording.recordingId) consumedRecordings.add(recording.recordingId);
          console.log("Recording synced and removed from store " + recording.date.getTime() + " for chat " + recording.chatId);
        } catch (e) {
          // Offline encounter still syncing → resolveRecordingChatId tagged it. This
          // is expected sync lag, not a failed upload: reschedule WITHOUT bumping
          // errorsCount (so a normally-syncing recording never trips the "failed to
          // upload" nag) and WITHOUT the raw-error log below (a pending case can carry
          // the axios error whose data holds base64 clinical audio — same reason Path A
          // avoids it). A session expiry keeps its re-login handling below; once the row
          // waits past the resolution ceiling it falls through so a never-syncing draft
          // is surfaced (errored) instead of retrying silently forever.
          // Server unreachable (outage/no internet). This says NOTHING about audio
          // integrity — the upload never left the device — so keep the row exactly
          // as it is: no errorsCount strike, no "error" state, and deliberately no
          // exhaustion ceiling (an outage may legitimately outlast any budget).
          // The next serverBackOnline / retry tick drains it untouched.
          if (e?.serverUnavailable) {
            anyPending = true;
            retryCheck();
            continue;
          }
          // Refused by the identity guard (client or server): park, no strike.
          if (e?.identityRefusal) {
            try {
              await parkIdentityRefusal(key, recording, e.identityRefusal);
            } catch (parkErr) {
              console.error("Could not park a refused recording", parkErr?.name || "");
            }
            if (recording.recordingId) parkedRecordingIds.add(recording.recordingId);
            continue;
          }
          if (e?.chatIdResolutionPending && !e?.sessionExpired && !chatIdResolutionExhausted(recording)) {
            anyPending = true;
            retryCheck();
            console.log("Offline encounter mapping not ready; rescheduling recording drain");
            continue;
          }
          // Same penalty-free lane for a save the webview refused offline (thrown
          // by assembleAndUploadSegments): connectivity, not corruption — no
          // errorsCount bump, no raw-error log, rows stay queued.
          if (e?.recordingSaveDeferredOffline) {
            anyPending = true;
            retryCheck();
            console.log("Recording save deferred (webview offline); rescheduling recording drain");
            continue;
          }
          // The recording was deleted while its segments were being stitched (the
          // modal's bulk delete, a sibling context). Nothing was uploaded, and the
          // row this iteration holds is gone with the rest — nothing to bump, nothing
          // pending, and the snapshot's remaining rows for it are stale.
          if (e?.recordingRowsGone) {
            if (recording.recordingId) consumedRecordings.add(recording.recordingId);
            console.log("Recording was deleted during assembly; nothing uploaded");
            continue;
          }
          console.error("Error syncing web recording", e);
          anyPending = true;
          // Session expired (403 "No challenge"): retrying the same request will
          // keep failing until the user signs in again, so tell them once. The
          // recording stays queued and uploads automatically after re-login.
          if (e?.sessionExpired && !sessionWarned.current) {
            sessionWarned.current = true;
            toast.error(t("hooks.useIndexDb.sessionExpiredRelogin"));
          }
          // Deleted encounter? Two-signal check: the save 404'd AND the chat
          // itself 404s (a bare 404 could be a deploy mishap; ambiguity stays
          // transient). Confirmed → park the row as orphaned (the skip at the
          // loop top stops the retry-forever loop) and tell the clinician
          // once. The audio row is KEPT — deletable/retryable in the modal.
          // Audio that cannot be decoded will not decode on the next pass either.
          // Park it exactly as a deleted encounter is parked: the row is KEPT (the
          // audio is not deleted, Retry in Local Recordings still clears the flag,
          // and Save audio still offers what is there), it simply stops being
          // re-attempted every backoff tick forever.
          let undecodable = false;
          if (e?.undecodableAudio) {
            undecodable = true;
            console.error("Recording audio could not be decoded; parking it (audio kept, no more retries)", {
              recordingId: recording.recordingId,
            });
            if (!undecodableWarned.current) {
              undecodableWarned.current = true;
              toast.error(t("hooks.useIndexDb.recordingUndecodable"));
            }
          }
          let orphaned = false;
          if (isHttp404(e) && (await confirmEncounterGone(api.getChat, recording.chatId))) {
            orphaned = true;
            console.log("Queued recording's encounter no longer exists — parking it (audio kept, no more retries)");
            if (!orphanWarned.current) {
              orphanWarned.current = true;
              toast.error(t("hooks.useIndexDb.recordingEncounterGone"));
            }
          }
          // Update error count and state in a fresh readwrite transaction.
          try {
            // Re-read the FULL row first. `recording` here is the pass's metadata
            // snapshot, which carries no segmentAudio/blobData — spreading THAT into
            // the put would write the row back stripped of its audio, i.e. turn a
            // failed upload into permanent data loss. (A row deleted meanwhile has
            // nothing to bump.)
            const current = await promisifyRequest(
              dbRef.current.transaction(["recordings"], "readonly").objectStore("recordings").get(key)
            );
            if (!current) continue;
            const updated = {
              ...current,
              errorsCount: (current.errorsCount || 0) + 1,
              state: "error",
              orphaned,
              undecodable,
              // "Attach anyway" covers one attempt; a failed one goes back to the guard.
              overrideIdentity: false,
            };
            const updTx = dbRef.current.transaction(["recordings"], "readwrite");
            const updStore = updTx.objectStore("recordings");
            await promisifyRequest(updStore.put(updated, key));
            console.log(
              "Updated recording " +
                updated.date.getTime() +
                " for chat " +
                updated.chatId +
                " with error count: " +
                updated.errorsCount
            );

            // Parking a SEGMENTED recording parks every one of its rows. The key
            // above is the final row (the one that drove the assembly); its siblings
            // carry the same dead audio and, left unflagged, they read as an
            // unfinished set — walked and counted every pass, and stranded when the
            // final row alone was purged at the TTL. Walked through the user's index
            // in ONE readwrite transaction, updating each row from cursor.value —
            // the FULL row, audio included; the metadata snapshot this pass holds
            // must never be spread into a write (it would strip the audio).
            if ((orphaned || undecodable) && recording.kind === "segment" && recording.recordingId) {
              parkedRecordingIds.add(recording.recordingId);
              await new Promise((resolve, reject) => {
                const parkTx = dbRef.current.transaction(["recordings"], "readwrite");
                const cursorReq = parkTx.objectStore("recordings").index("userId").openCursor(IDBKeyRange.only(auth.user.id));
                cursorReq.onsuccess = (event) => {
                  const cursor = event.target.result;
                  if (!cursor) return;
                  const row = cursor.value;
                  if (row && row.kind === "segment" && row.recordingId === recording.recordingId && cursor.primaryKey !== key) {
                    cursor.update({ ...row, orphaned, undecodable });
                  }
                  cursor.continue();
                };
                cursorReq.onerror = (event) => reject(event.target?.error || event);
                parkTx.oncomplete = () => resolve();
                parkTx.onabort = (event) => reject(parkTx.error || event.target?.error || event);
                parkTx.onerror = (event) => reject(parkTx.error || event.target?.error || event);
              });
            }

            // (No per-recording toast here — the post-loop summary toast below
            // consolidates all still-pending recordings into one notice.)
          } catch (updateError) {
            // A failure to bump the error counter must NEVER cost us the audio.
            // Leave the row exactly as it is — the next drain retries it. (This
            // used to delete the row, turning a transient IDB hiccup into
            // permanent audio loss.)
            console.error("Failed to update error count; leaving recording queued for retry", updateError);
            // (No per-recording toast here either — see the post-loop summary.)
          }
        }
      }

      // Refresh the modal/badge snapshot from the post-drain store state.
      if (!alive.current) return;
      const remaining = await listRecordings();
      setRecordings((prev) => (sameRecordingList(prev, remaining) ? prev : remaining));
      // The sign-in question. A pass has now run, so what is left is a genuine
      // backlog rather than a queue that had not been given its chance. Asked ONCE
      // per sign-in (the effect that starts the drain arms it) by the first pass
      // that runs with no capture active in this tab — a capture in progress leaves
      // it armed for the pass after the recording ends, rather than popping a modal
      // over the dictation. Never answered from a partial scan: the amount it leads
      // with would be wrong. Interrupt at most once per session when the device is
      // holding a meaningful amount of un-synced clinical audio; the summary toast
      // below stays the right weight for anything smaller.
      if (backlogCheckArmed.current && !partialScan && !getActiveRecording()) {
        backlogCheckArmed.current = false;
        if (!backlogAlerted.current && queuedAudioSecs(remaining) >= BACKLOG_ALERT_SECS) {
          backlogAlerted.current = true;
          setShowBacklogAlert(true);
        }
      }
      // An incomplete view is not a drained queue: come back for the rows the scan
      // could not read.
      if (partialScan) anyPending = true;
      // Recordings that have genuinely given up (>= WARN_AFTER_ERRORS attempts, i.e.
      // status "error"). A single transient failure (still "retrying") shouldn't
      // proactively nag — this matches the threshold of the per-recording warning
      // this summary replaced.
      const stuckCount = remaining.filter((r) => r.status === "error").length;

      if (madeProgress) {
        retryAttempt.current = 0;
        sessionWarned.current = false;
        // Re-arm the summary only when the queue has FULLY drained, so a slow
        // partial drain doesn't re-pop the toast on every backoff tick. A newly
        // captured recording (addRecording/addRecordingSegment) also re-arms it.
        if (remaining.length === 0) summaryShown.current = false;
      }
      if (anyPending) {
        retryCheck();
        // ONE actionable notice, fired once per failing session and only once at
        // least one recording is genuinely stuck (not on a first transient
        // failure). Clicking it opens the Local Recordings modal.
        if (!summaryShown.current && stuckCount > 0) {
          summaryShown.current = true;
          toast.warning(t("hooks.useIndexDb.recordingsUploadSummary", { count: remaining.length }), {
            toastId: "recordings-upload-summary",
            onClick: () => setShowRecordingsModal(true),
            autoClose: 6000,
          });
        }
      }
    } catch (event) {
      // Silent only when the provider is already gone — nobody is left to act on it.
      if (alive.current) console.error("Error fetching or processing recordings from the store", event?.target?.error || event);
      // A top-level failure here (stale IDB handle after a network flap, a read that
      // throws) must not strand queued recordings with no future trigger. Reschedule
      // so the queue self-heals instead of waiting for the next online/visibility event.
      retryCheck();
    } finally {
      syncing.current = false;
      // A trigger arrived while we were busy — drain again now that the queue is
      // free, instead of leaving it for the next backoff/event.
      if (rescanRequested.current) {
        rescanRequested.current = false;
        checkRecordings();
      }
    }
  };

  // Return
  return {
    addRecording,
    addRecordingSegment,
    exportRecording,
    recordings,
    showRecordingsModal,
    setShowRecordingsModal,
    refreshRecordings,
    retryRecording,
    retryAll,
    deleteRecording,
    deleteRecordings,
    showBacklogAlert,
    setShowBacklogAlert,
    bulkSave,
    setBulkSave,
  };
}

export function ProvideIndexDb({ children }) {
  const values = useIndexDbProvider();
  return <IndexDbContext.Provider value={values}>{children}</IndexDbContext.Provider>;
}

export const useIndexDb = () => {
  return useContext(IndexDbContext);
};
